import React, { useState, useEffect, useRef, forwardRef } from 'react';
import ReactDOM from 'react-dom';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import { Column } from '../../../../core/components/InlineEditTable/types';
import { PILL_COLORS } from '../QuickTasks/constants';
import DropdownMenuPortal from '../QuickTasks/components/DropdownMenuPortal';
import TeamMemberInline from '../QuickTasks/components/TeamMemberInline';
import { projectStatusOptions } from '../../models';
import { IoChevronDownOutline } from 'react-icons/io5';
import { BsCalendar4Week } from 'react-icons/bs';

export const PRIORITY_OPTIONS = ['Low', 'Medium', 'High', 'Urgent'];

const InlineTextEdit = ({
  value,
  onSave,
  placeholder,
}: {
  value: string;
  onSave: (nextValue: string) => void;
  placeholder?: string;
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing) {
      setDraft(value || '');
    }
  }, [value, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const commit = () => {
    setEditing(false);
    if (draft !== (value || '')) {
      onSave(draft);
    }
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        className="form-control"
        style={{ minWidth: 140, height: 30, padding: '2px 6px' }}
        value={draft}
        placeholder={placeholder}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          }
          if (e.key === 'Escape') {
            e.preventDefault();
            setEditing(false);
            setDraft(value || '');
          }
        }}
      />
    );
  }

  return (
    <button
      type="button"
      className="btn btn-link p-0"
      onClick={(e) => {
        e.stopPropagation();
        setEditing(true);
      }}
      style={{
        textDecoration: 'none',
        width: '100%',
        textAlign: 'left',
        fontSize: 'inherit',
        color: '#0f172a',
        fontWeight: 600,
        maxWidth: 120,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
      title={value || ''}
    >
      {value || placeholder || '-'}
    </button>
  );
};

export const InlineSelectMenu = ({
  value,
  options,
  onSelect,
  renderValue,
  minWidth,
}: {
  value: string;
  options: string[];
  onSelect: (nextValue: string) => void;
  renderValue: (value: string) => React.ReactNode;
  minWidth?: number;
}) => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const choose = (nextValue: string) => {
    setOpen(false);
    if (nextValue !== value) onSelect(nextValue);
  };
  return (
    <div className="inline-select" style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        className="btn btn-link p-0"
        ref={btnRef}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((prev) => !prev);
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{ textDecoration: 'none' }}
      >
        {renderValue(value)}
        <IoChevronDownOutline />
      </button>
      <DropdownMenuPortal
        anchorRef={btnRef}
        open={open}
        onClose={() => setOpen(false)}
        minWidth={minWidth}
      >
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            className={`dropdown-item${opt === value ? ' active' : ''}`}
            onClick={(event) => {
              event.stopPropagation();
              choose(opt);
            }}
          >
            {opt}
          </button>
        ))}
      </DropdownMenuPortal>
    </div>
  );
};

export const renderStatusPill = (statusText: string) => {
  const text = statusText || '';
  const lower = String(text).toLowerCase();
  let bg = '#ccffe2';
  let fg = '#34c673';
  if (lower.includes('progress')) {
    bg = '#ffd1f7';
    fg = '#fc52df';
  } else if (lower === 'new' || lower.includes('to do') || lower.includes('todo')) {
    bg = '#d1d8ff';
    fg = '#5c75ff';
  } else if (lower.includes('planning') || lower.includes('hold')) {
    bg = '#ffe8d1';
    fg = '#f7830f';
  } else if (lower.includes('review')) {
    bg = '#f3e8ff';
    fg = '#7c3aed';
  } else if (lower === 'incomplete') {
    bg = '#e5e7eb';
    fg = '#4b5563';
  } else if (lower.includes('completed')) {
    bg = '#ccffe2';
    fg = '#34c673';
  }
  return (
    <span
      className="task-status-pill"
      style={{
        background: bg,
        color: fg,
      }}
    >
      {text}
    </span>
  );
};

export const renderPriorityPill = (priority: string) => {
  const text = String(priority || '').toLowerCase();
  const color = PILL_COLORS[text] || PILL_COLORS.low;
  return (
    <span
      className="task-status-pill"
      style={{
        background: color.bg,
        color: color.fg,
      }}
    >
      {priority || 'Low'}
    </span>
  );
};

export type ColumnOptions = {
  intl: any;
  employees: Array<{ id: number | string; firstName: string; lastName: string }>;
  prefixes?: Array<{ type: number | string; message: string }>;
  onMemberChange?: (row: any, nextValue: string | number | '') => void;
  onStatusChange?: (row: any, nextValue: string) => void;
  onPriorityChange?: (row: any, nextValue: string) => void;
  onDueDateChange?: (row: any, date: Date | null) => void;
};

const DueDateButton = forwardRef(
  ({ value, onClick }: { value?: string; onClick?: (event: any) => void }, ref) => (
    <span className="inline-select inline-calendar">
      <button
        type="button"
        className="btn btn-link p-0"
        ref={ref as any}
        onClick={(event) => {
          event.stopPropagation();
          if (onClick) onClick(event);
        }}
        style={{ textDecoration: 'none', fontSize: 'inherit', color: '#0f172a' }}
      >
        <BsCalendar4Week />
        {value || '-'}
      </button>
    </span>
  )
);

const InlineDueDateCell = ({ rowData, mixIns }: { rowData: any; mixIns: any }) => {
  const rawDate = rowData?.dueDate || '';
  const parsed = moment(rawDate, 'MM/DD/YYYY', true);
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    parsed.isValid() ? parsed.toDate() : null
  );

  useEffect(() => {
    const next = moment(rawDate, 'MM/DD/YYYY', true);
    setSelectedDate(next.isValid() ? next.toDate() : null);
  }, [rawDate]);

  const handleChange = (date: Date | null) => {
    setSelectedDate(date);
    if (typeof mixIns?.onSaveDate === 'function') {
      mixIns.onSaveDate(rowData, date);
    }
  };

  const displayDate = selectedDate ? moment(selectedDate).format('MM/DD/YYYY') : '-';

  return (
    <DatePicker
      selected={selectedDate}
      onChange={handleChange}
      customInput={<DueDateButton value={displayDate} />}
      calendarClassName="qt-datepicker"
      popperClassName="qt-datepicker-popper"
      popperPlacement="bottom-start"
      dateFormat="MM/dd/yyyy"
      shouldCloseOnScroll={false}
      popperModifiers={[]}
      popperContainer={({ children }) =>
        ReactDOM.createPortal(
          <div
            className="qt-datepicker-portal"
            style={{ zIndex: 2000, minWidth: 240 }}
            onClick={(event) => event.stopPropagation()}
          >
            {children}
          </div>,
          document.body
        )
      }
    />
  );
};

export const EntryDueDate = ({
  value,
  onChange,
}: {
  value: any;
  onChange: (date: Date | null) => void;
}) => {
  const selected = value instanceof Date ? value : null;
  const display = selected ? moment(selected).format('MM/DD/YYYY') : '';
  return (
    <DatePicker
      selected={selected}
      onChange={(date: Date | null) => onChange(date)}
      customInput={<DueDateButton value={display || 'Set date'} />}
      calendarClassName="qt-datepicker"
      popperClassName="qt-datepicker-popper"
      popperPlacement="bottom-start"
      dateFormat="MM/dd/yyyy"
      shouldCloseOnScroll={false}
      popperModifiers={[]}
      popperContainer={({ children }) =>
        ReactDOM.createPortal(
          <div
            className="qt-datepicker-portal"
            style={{ zIndex: 2000, minWidth: 240 }}
            onClick={(event) => event.stopPropagation()}
          >
            {children}
          </div>,
          document.body
        )
      }
    />
  );
};

export const SORT_KEY_MAP: Record<string, string> = {
  status: 'status',
  prefix: 'prefixId',
  name: 'name',
  dueDate: 'dueDate',
  teamMember: 'employeeId',
  priority: 'priority',
  created: 'createdAt',
};

export const columns = (options: ColumnOptions): Column[] => [
  {
    title: options.intl.formatMessage({ id: 'dueDate', defaultMessage: 'Due Date' }),
    keyName: 'dueDate',
    sortBy: true,
    sortValue: (row) => row._dueDate,
    align: 'left',
    cell: {
      component: InlineDueDateCell,
      mixIns: {
        onSaveDate: (row: any, date: Date | null) => {
          if (options.onDueDateChange) {
            options.onDueDateChange(row, date);
          }
        },
      },
    },
  },
  {
    title: options.intl.formatMessage({ id: 'prefix', defaultMessage: 'Prefix' }),
    keyName: 'prefix',
    sortBy: true,
    align: 'right',
  },
  {
    title: options.intl.formatMessage({ id: 'name', defaultMessage: 'Name' }),
    keyName: 'name',
    sortBy: true,
    align: 'left',
  },
  {
    title: options.intl.formatMessage({ id: 'instructions', defaultMessage: 'Instructions' }),
    keyName: 'description',
    sortBy: true,
    align: 'left',
    customComponent: (row: any) => {
      return <span style={{ color: '#475569' }}>{row?.description || '-'}</span>;
    },
  },
  {
    title: options.intl.formatMessage({ id: 'status', defaultMessage: 'Status' }),
    keyName: 'status',
    sortBy: true,
    align: 'left',
    editable: true,
    editHandler: (row, newRow, isEditMode, setEditMode, index, editCell, setEditCell, mutate) => {
      return (
        <InlineSelectMenu
          value={row?.status || 'New'}
          options={projectStatusOptions}
          renderValue={(value) => renderStatusPill(value || 'New')}
          onSelect={(nextValue) => {
            mutate({ ...row, status: nextValue });
            if (options.onStatusChange) {
              options.onStatusChange(row, nextValue);
            }
          }}
        />
      );
    },
    customComponent: (row: any) => {
      return renderStatusPill(row?.status || '');
    },
  },
  {
    title: options.intl.formatMessage({ id: 'priority', defaultMessage: 'Priority' }),
    keyName: 'priority',
    sortBy: true,
    sortValue: (row) => row._priority,
    align: 'left',
    editable: true,
    editHandler: (row, newRow, isEditMode, setEditMode, index, editCell, setEditCell, mutate) => {
      return (
        <InlineSelectMenu
          value={row?.priority || 'Low'}
          options={PRIORITY_OPTIONS}
          renderValue={(value) => renderPriorityPill(value || 'Low')}
          onSelect={(nextValue) => {
            mutate({ ...row, priority: nextValue });
            if (options.onPriorityChange) {
              options.onPriorityChange(row, nextValue);
            }
          }}
        />
      );
    },
    customComponent: (row: any) => {
      return renderPriorityPill(row?.priority || 'Low');
    },
  },
  {
    title: options.intl.formatMessage({ id: 'assignedTo', defaultMessage: 'Assigned to' }),
    keyName: 'teamMember',
    sortBy: true,
    align: 'left',
    customComponent: (row: any) => {
      return (
        <TeamMemberInline
          key={`${row?.id}-${row?.employeeId || 'unassigned'}`}
          rowData={{
            _taskId: row?.id,
            employeeId: row?.employeeId || '',
            label: row?.teamMember || '',
          }}
          employees={options.employees || []}
          intl={options.intl}
          onChange={(rowData, nextValue) => {
            if (options.onMemberChange) {
              options.onMemberChange(row, nextValue);
            }
          }}
        />
      );
    },
  },
  {
    title: options.intl.formatMessage({ id: 'dateCreated', defaultMessage: 'Date Created' }),
    keyName: 'created',
    sortBy: true,
    sortValue: (row) => row._created,
    align: 'left',
  },
];
